import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { X, Plus, Trash2 } from "lucide-react";
import {
  createQuestion,
  updateQuestion,
  fetchQuestions,
} from "../../redux/slices/questionSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { useToast } from "../../components/ui/use-toast";
import { MultilingualInput, emptyMultilingual } from "../../components/common/MultilingualInput";

const QuestionDialog = ({ open, onClose, onSaved, question, categories }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    category_id: "",
    question_text: emptyMultilingual(),
    question_type: "mcq",
    options: [emptyMultilingual(), emptyMultilingual()],
    correct_answers: [],
    explanation: emptyMultilingual(),
    default_marks: 5,
    default_time_seconds: 30,
    difficulty: "medium",
    tags: [],
    source: "",
  });

  const [tagInput, setTagInput] = useState("");

  // Flatten nested categories for dropdown
  const flattenCategories = (cats, result = [], level = 0) => {
    cats.forEach((cat) => {
      result.push({ ...cat, level });
      if (cat.children && cat.children.length > 0) {
        flattenCategories(cat.children, result, level + 1);
      }
    });
    return result;
  };

  const flatCategories = flattenCategories(categories || []);

  useEffect(() => {
    if (question) {
      setFormData({
        category_id: question.category_id || "",
        question_text: question.question_text || emptyMultilingual(),
        question_type: question.question_type || "mcq",
        options: question.options || [
          emptyMultilingual(),
          emptyMultilingual(),
        ],
        correct_answers: question.correct_answers || [],
        explanation: question.explanation || emptyMultilingual(),
        default_marks: question.default_marks || 5,
        default_time_seconds: question.default_time_seconds || 30,
        difficulty: question.difficulty || "medium",
        tags: question.tags || [],
        source: question.source || "",
      });
    } else {
      // Reset for new question
      setFormData({
        category_id: "",
        question_text: emptyMultilingual(),
        question_type: "mcq",
        options: [emptyMultilingual(), emptyMultilingual()],
        correct_answers: [],
        explanation: emptyMultilingual(),
        default_marks: 5,
        default_time_seconds: 30,
        difficulty: "medium",
        tags: [],
        source: "",
      });
    }
  }, [question, open]);

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, emptyMultilingual()],
    });
  };

  const handleRemoveOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    const newCorrectAnswers = formData.correct_answers
      .filter((ans) => ans !== index)
      .map((ans) => (ans > index ? ans - 1 : ans));
    setFormData({
      ...formData,
      options: newOptions,
      correct_answers: newCorrectAnswers,
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleCorrectAnswerToggle = (index) => {
    const isMultipleSelect = formData.question_type === "multiple_select";
    let newCorrectAnswers;

    if (isMultipleSelect) {
      if (formData.correct_answers.includes(index)) {
        newCorrectAnswers = formData.correct_answers.filter((i) => i !== index);
      } else {
        newCorrectAnswers = [...formData.correct_answers, index];
      }
    } else {
      newCorrectAnswers = [index];
    }

    setFormData({ ...formData, correct_answers: newCorrectAnswers });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((t) => t !== tag),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.category_id) {
      toast({
        title: "Error",
        description: "Please select a category",
        variant: "destructive",
      });
      return;
    }

    if (!formData.question_text.en.trim()) {
      toast({
        title: "Error",
        description: "Please enter question text in English",
        variant: "destructive",
      });
      return;
    }

    if (formData.options.length < 2) {
      toast({
        title: "Error",
        description: "Please add at least 2 options",
        variant: "destructive",
      });
      return;
    }

    if (formData.correct_answers.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one correct answer",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        category_id: formData.category_id,
      };
      if (question) {
        await dispatch(
          updateQuestion({ id: question.id, data: payload })
        ).unwrap();
        toast({
          title: "Success",
          description: "Question updated successfully",
        });
      } else {
        await dispatch(createQuestion(payload)).unwrap();
        toast({
          title: "Success",
          description: "Question created successfully",
        });
      }
      if (onSaved) {
        await onSaved();
      } else {
        dispatch(fetchQuestions());
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to save question",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {question ? "Edit Question" : "Create New Question"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Home category — practice mappings are managed on Categories */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <div>
              <h3 className="font-medium text-sm">Home category</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Organizes this question in the bank. Map it to additional
                categories for practice from the Categories page.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, category_id: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {flatCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      <span style={{ paddingLeft: `${category.level * 16}px` }}>
                        {category.level > 0 && "└ "}
                        {category.name?.en || "Unnamed"}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Question Text */}
          <div className="space-y-2">
            <MultilingualInput
              label="Question Text *"
              value={formData.question_text}
              onChange={(value) =>
                setFormData({ ...formData, question_text: value })
              }
              multiline={true}
              rows={3}
              required
            />
          </div>

          {/* Question Type & Difficulty */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Question Type *</Label>
              <Select
                value={formData.question_type}
                onValueChange={(value) =>
                  setFormData({ ...formData, question_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mcq">Multiple Choice (Single)</SelectItem>
                  <SelectItem value="multiple_select">
                    Multiple Select
                  </SelectItem>
                  <SelectItem value="true_false">True/False</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Difficulty *</Label>
              <Select
                value={formData.difficulty}
                onValueChange={(value) =>
                  setFormData({ ...formData, difficulty: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Options *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddOption}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Option
              </Button>
            </div>
            <div className="space-y-3">
              {formData.options.map((option, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Checkbox
                    checked={formData.correct_answers.includes(index)}
                    onCheckedChange={() => handleCorrectAnswerToggle(index)}
                    className="mt-3"
                  />
                  <div className="flex-1">
                    <MultilingualInput
                      value={option}
                      onChange={(value) => handleOptionChange(index, value)}
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                  {formData.options.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveOption(index)}
                      className="mt-2"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              Check the box to mark correct answer(s)
            </p>
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <MultilingualInput
              label="Explanation (Optional)"
              value={formData.explanation}
              onChange={(value) =>
                setFormData({ ...formData, explanation: value })
              }
              multiline={true}
              rows={2}
            />
          </div>

          {/* Marks & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Marks</Label>
              <Input
                type="number"
                min="1"
                value={formData.default_marks}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_marks: parseInt(e.target.value) || 5,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Default Time (seconds)</Label>
              <Input
                type="number"
                min="1"
                value={formData.default_time_seconds}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_time_seconds: parseInt(e.target.value) || 30,
                  })
                }
              />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags (Optional)</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddTag())
                }
                placeholder="Add tags..."
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag, index) => (
                <div
                  key={index}
                  className="bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-sm flex items-center gap-1"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label>Source (Optional)</Label>
            <Input
              value={formData.source}
              onChange={(e) =>
                setFormData({ ...formData, source: e.target.value })
              }
              placeholder="e.g., Surah Al-Baqarah 2:255"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {question ? "Update Question" : "Create Question"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionDialog;
